'use client';

import { useState, useEffect, useMemo, memo } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import {
    api,
    CategoryData,
    WeightCluster,
    CertificationScore,
} from '@/lib/api';
import {
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    ResponsiveContainer,
    Legend,
    Tooltip,
} from 'recharts';
import { useCertificationComparisonContext } from '@/contexts/CertificationComparisonContext';
import { CertificationComparisonSelector } from './CertificationComparisonSelector';
import { useScoreRefresh } from '@/contexts/ScoreRefreshContext';

interface ScoreReportProps {
    certificationId: string;
}

// Cluster colors for the bar chart
const CLUSTER_COLORS: Record<string, string> = {
    'ENVIRONMENT': '#22c55e', // green
    'DESIGN': '#3b82f6',      // blue
    'ECONOMIC': '#f59e0b',    // amber/orange
    'SOCIAL': '#f97316',      // orange
};

// Comparison overlay colors — must contrast with each other and with cluster colors
const COMPARISON_COLORS = ['#0d9488', '#7c3aed'] as const; // teal-600, violet-700

// Cluster background colors for the table
const CLUSTER_BG_COLORS: Record<string, string> = {
    'ENVIRONMENT': 'bg-green-50',
    'DESIGN': 'bg-blue-50',
    'ECONOMIC': 'bg-amber-50',
    'SOCIAL': 'bg-orange-50',
};

const CLUSTER_BORDER_COLORS: Record<string, string> = {
    'ENVIRONMENT': 'border-green-200',
    'DESIGN': 'border-blue-200',
    'ECONOMIC': 'border-amber-200',
    'SOCIAL': 'border-orange-200',
};

// Grading system based on overall score
function getGrade(score: number | null): { grade: string; color: string; bgColor: string } {
    if (score === null) return { grade: '—', color: 'text-slate-400', bgColor: 'bg-slate-100' };
    if (score >= 4.5) return { grade: 'A', color: 'text-emerald-700', bgColor: 'bg-emerald-100' };
    if (score >= 4) return { grade: 'B', color: 'text-green-700', bgColor: 'bg-green-100' };
    if (score >= 3) return { grade: 'C', color: 'text-yellow-700', bgColor: 'bg-yellow-100' };
    if (score >= 2) return { grade: 'D', color: 'text-orange-700', bgColor: 'bg-orange-100' };
    return { grade: 'E', color: 'text-red-700', bgColor: 'bg-red-100' };
}

interface ScoreRow {
    cluster: string;
    category: string;
    subcategory: string;
    enCategory: string;     // English category name for API matching
    enSubcategory: string;  // English subcategory name for API matching
    score: number | null;
    reliability: number;
    effectiveScore: number | null;
}

interface CategoryScore {
    category: string;
    score: number | null;
}

const ScoreReportComponent = ({ certificationId }: ScoreReportProps) => {
    const locale = useLocale();
    const t = useTranslations('certification.scoreReport');
    const tStage2 = useTranslations('certification.stage2');
    const tComp = useTranslations('certification.comparison');
    const [scores, setScores] = useState<CertificationScore[]>([]);
    const [categoryData, setCategoryData] = useState<Record<string, CategoryData[]>>({});
    const [enCategoryData, setEnCategoryData] = useState<Record<string, CategoryData[]>>({});
    const [weightsData, setWeightsData] = useState<WeightCluster[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<{ cluster: string; category: string } | null>(null);
    const { refreshTrigger, weightProfileForClear } = useScoreRefresh();

    // Comparison context
    const {
        visibleComparisons,
        comparisonCerts,
        toggleVisibility,
        removeComparison,
        fetchAvailableCertifications,
        filterMode,
        setFilterMode,
        weightProfileId,
        hideSelectionControls,
    } = useCertificationComparisonContext();

    // Resolved color per cert (custom or default)
    const comparisonColorMap = useMemo(() =>
        new Map(comparisonCerts.map((c, idx) => [c.id, c.color || COMPARISON_COLORS[idx % COMPARISON_COLORS.length]])),
        [comparisonCerts]
    );



    // Load static data (run once)
    useEffect(() => {
        const loadStaticData = async () => {
            setLoading(true);
            try {
                // Load locale data AND English data for name mapping
                const lang = locale === 'pt' ? 'pt' : 'en';
                const [
                    envRes, designRes, econRes, socialRes,
                    envEnRes, designEnRes, econEnRes, socialEnRes,
                    weightsRes
                ] = await Promise.all([
                    // Locale versions (for display)
                    fetch(`/api/scores-data/environment?lang=${lang}`),
                    fetch(`/api/scores-data/design?lang=${lang}`),
                    fetch(`/api/scores-data/economic?lang=${lang}`),
                    fetch(`/api/scores-data/social?lang=${lang}`),
                    // English versions (for score key mapping)
                    fetch(`/api/scores-data/environment?lang=en`),
                    fetch(`/api/scores-data/design?lang=en`),
                    fetch(`/api/scores-data/economic?lang=en`),
                    fetch(`/api/scores-data/social?lang=en`),
                    // Weights (always English)
                    fetch(`/api/weights-data?lang=en`),
                ]);

                const envData = await envRes.json();
                const designData = await designRes.json();
                const econData = await econRes.json();
                const socialData = await socialRes.json();

                const envEnData = await envEnRes.json();
                const designEnData = await designEnRes.json();
                const econEnData = await econEnRes.json();
                const socialEnData = await socialEnRes.json();

                const weights = await weightsRes.json();

                setCategoryData({
                    'ENVIRONMENT': envData,
                    'DESIGN': designData,
                    'ECONOMIC': econData,
                    'SOCIAL': socialData,
                });
                setEnCategoryData({
                    'ENVIRONMENT': envEnData,
                    'DESIGN': designEnData,
                    'ECONOMIC': econEnData,
                    'SOCIAL': socialEnData,
                });
                setWeightsData(weights);
            } catch (error) {
                console.error('Failed to load static report data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadStaticData();
    }, [locale]); // Only re-run if locale changes

    // Load scores (run on mount and when refresh triggered)
    useEffect(() => {
        const loadScores = async () => {
            try {
                const scoresRes = await api.getCertificationScores(certificationId);
                setScores(scoresRes.scores);
            } catch (error) {
                console.error('Failed to load scores:', error);
            }
        };

        loadScores();
    }, [certificationId, refreshTrigger]);

    // Clear scores selectively when weightProfileForClear changes
    useEffect(() => {
        if (!weightProfileForClear || !weightProfileForClear.weight_tree) return;

        console.log('ScoreReport: Selectively clearing scores based on new weight profile');

        // Build a set of subcategories that have 0% weight in the new profile
        const zeroWeightKeys = new Set<string>();

        weightProfileForClear.weight_tree.forEach((clusterNode: any) => {
            // If cluster has 0 weight, all its children are disabled
            if (clusterNode.weighting === 0 || clusterNode.global_weight === 0) {
                clusterNode.children?.forEach((categoryNode: any) => {
                    categoryNode.children?.forEach((subcategoryNode: any) => {
                        const key = `${clusterNode.name_en}::${categoryNode.name_en}::${subcategoryNode.name_en}`;
                        zeroWeightKeys.add(key);
                    });
                });
            } else {
                // Check category level
                clusterNode.children?.forEach((categoryNode: any) => {
                    if (categoryNode.weighting === 0 || categoryNode.global_weight === 0) {
                        categoryNode.children?.forEach((subcategoryNode: any) => {
                            const key = `${clusterNode.name_en}::${categoryNode.name_en}::${subcategoryNode.name_en}`;
                            zeroWeightKeys.add(key);
                        });
                    } else {
                        // Check subcategory level
                        categoryNode.children?.forEach((subcategoryNode: any) => {
                            if (subcategoryNode.weighting === 0 || subcategoryNode.global_weight === 0) {
                                const key = `${clusterNode.name_en}::${categoryNode.name_en}::${subcategoryNode.name_en}`;
                                zeroWeightKeys.add(key);
                            }
                        });
                    }
                });
            }
        });

        console.log('ScoreReport: Found', zeroWeightKeys.size, 'subcategories with 0% weight');

        // Clear scores for zero-weight subcategories
        setScores(prev => {
            return prev.map(score => {
                const key = `${score.cluster}::${score.category_name}::${score.subcategory_name}`;
                if (zeroWeightKeys.has(key)) {
                    return { ...score, score: null, reliability: 1.0, raw_value: null };
                }
                return score;
            });
        });
    }, [weightProfileForClear]);

    // Build the score rows for the table
    const scoreRows = useMemo(() => {
        const rows: ScoreRow[] = [];
        const scoreMap = new Map<string, CertificationScore>();

        for (const s of scores) {
            const key = `${s.cluster}::${s.category_name}::${s.subcategory_name}`;
            scoreMap.set(key, s);
        }

        const clusterOrder = ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL'];

        for (const cluster of clusterOrder) {
            const categories = categoryData[cluster] || [];
            const enCategories = enCategoryData[cluster] || [];

            for (let i = 0; i < categories.length && i < enCategories.length; i++) {
                const cat = categories[i];
                const enCat = enCategories[i];

                for (let j = 0; j < cat.subcategories.length && j < enCat.subcategories.length; j++) {
                    const sub = cat.subcategories[j];
                    const enSub = enCat.subcategories[j];

                    // Use English names for score lookup (scores from API use English names)
                    const key = `${cluster}::${enCat.category}::${enSub.name}`;
                    const scoreData = scoreMap.get(key);

                    rows.push({
                        cluster,
                        category: cat.category, // Display locale name
                        subcategory: sub.name,  // Display locale name
                        enCategory: enCat.category,     // English name for API
                        enSubcategory: enSub.name,       // English name for API
                        score: scoreData?.score ?? null,
                        reliability: scoreData?.reliability ?? 1.0,
                        effectiveScore: scoreData?.score !== null && scoreData?.score !== undefined
                            ? Math.round(scoreData.score * scoreData.reliability * 10) / 10
                            : null,
                    });
                }
            }
        }

        return rows;
    }, [scores, categoryData, enCategoryData]);

    // Calculate cluster aggregated scores
    const clusterScores = useMemo(() => {
        const result: Record<string, { score: number | null; count: number; total: number }> = {};

        for (const cluster of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
            const clusterRows = scoreRows.filter(r => r.cluster === cluster);
            const scoredRows = clusterRows.filter(r => r.effectiveScore !== null);

            if (scoredRows.length > 0) {
                const sum = scoredRows.reduce((acc, r) => acc + (r.effectiveScore || 0), 0);
                result[cluster] = {
                    score: Math.round((sum / scoredRows.length) * 10) / 10,
                    count: scoredRows.length,
                    total: clusterRows.length,
                };
            } else {
                result[cluster] = { score: null, count: 0, total: clusterRows.length };
            }
        }

        return result;
    }, [scoreRows]);

    // Calculate overall score
    const overallScore = useMemo(() => {
        const scoredClusters = Object.values(clusterScores).filter(c => c.score !== null);
        if (scoredClusters.length === 0) return null;

        const sum = scoredClusters.reduce((acc, c) => acc + (c.score || 0), 0);
        return Math.round((sum / scoredClusters.length) * 10) / 10;
    }, [clusterScores]);

    // Calculate overall + cluster scores for each comparison cert
    const comparisonSummaryScores = useMemo(() => {
        return visibleComparisons.map((comp) => {
            const compScoreMap = new Map<string, CertificationScore>();
            for (const s of comp.scores) {
                const key = `${s.cluster}::${s.category_name}::${s.subcategory_name}`;
                compScoreMap.set(key, s);
            }

            const clusters: Record<string, { score: number | null; count: number; total: number }> = {};
            for (const cluster of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
                const enCategories = enCategoryData[cluster] || [];
                let total = 0, count = 0, rowCount = 0;
                for (const enCat of enCategories) {
                    for (const enSub of enCat.subcategories) {
                        rowCount++;
                        const key = `${cluster}::${enCat.category}::${enSub.name}`;
                        const s = compScoreMap.get(key);
                        if (s?.score != null) { total += s.score * s.reliability; count++; }
                    }
                }
                clusters[cluster] = { score: count > 0 ? Math.round((total / count) * 10) / 10 : null, count, total: rowCount };
            }

            const scoredClusters = Object.values(clusters).filter(c => c.score !== null);
            const overall = scoredClusters.length > 0
                ? Math.round((scoredClusters.reduce((a, c) => a + (c.score || 0), 0) / scoredClusters.length) * 10) / 10
                : null;

            return { id: comp.id, name: comp.name, overall, clusters };
        });
    }, [visibleComparisons, enCategoryData]);

    // Calculate category scores for radar charts (by cluster)
    const categoryScoresByCluster = useMemo(() => {
        const result: Record<string, CategoryScore[]> = {};

        for (const cluster of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
            const categories = categoryData[cluster] || [];
            const clusterCategoryScores: CategoryScore[] = [];

            for (const cat of categories) {
                const catRows = scoreRows.filter(r => r.cluster === cluster && r.category === cat.category);
                const scoredRows = catRows.filter(r => r.effectiveScore !== null);

                if (scoredRows.length > 0) {
                    const sum = scoredRows.reduce((acc, r) => acc + (r.effectiveScore || 0), 0);
                    clusterCategoryScores.push({
                        category: cat.category,
                        score: Math.round((sum / scoredRows.length) * 10) / 10,
                    });
                } else {
                    clusterCategoryScores.push({
                        category: cat.category,
                        score: null,
                    });
                }
            }

            result[cluster] = clusterCategoryScores;
        }

        return result;
    }, [scoreRows, categoryData]);

    // Calculate comparison category scores for radar charts
    const comparisonCategoryScoresByCluster = useMemo(() => {
        const result: Map<string, Record<string, CategoryScore[]>> = new Map();

        for (const comp of visibleComparisons) {
            const compResult: Record<string, CategoryScore[]> = {};

            // Build score map for this comparison
            const compScoreMap = new Map<string, CertificationScore>();
            for (const s of comp.scores) {
                const key = `${s.cluster}::${s.category_name}::${s.subcategory_name}`;
                compScoreMap.set(key, s);
            }

            for (const cluster of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
                const categories = categoryData[cluster] || [];
                const enCategories = enCategoryData[cluster] || [];
                const clusterCategoryScores: CategoryScore[] = [];

                for (let i = 0; i < categories.length && i < enCategories.length; i++) {
                    const cat = categories[i];
                    const enCat = enCategories[i];

                    let catTotal = 0;
                    let catCount = 0;

                    for (let j = 0; j < cat.subcategories.length && j < enCat.subcategories.length; j++) {
                        const enSub = enCat.subcategories[j];
                        const key = `${cluster}::${enCat.category}::${enSub.name}`;
                        const scoreData = compScoreMap.get(key);

                        if (scoreData?.score != null) {
                            const effectiveScore = scoreData.score * scoreData.reliability;
                            catTotal += effectiveScore;
                            catCount++;
                        }
                    }

                    clusterCategoryScores.push({
                        category: cat.category,
                        score: catCount > 0 ? Math.round((catTotal / catCount) * 10) / 10 : null,
                    });
                }

                compResult[cluster] = clusterCategoryScores;
            }

            result.set(comp.id, compResult);
        }

        return result;
    }, [visibleComparisons, categoryData, enCategoryData]);

    // Prepare radar chart data for each cluster (including comparisons)
    const radarDataByCluster = useMemo(() => {
        const result: Record<string, Array<{ category: string; score: number; fullMark: number;[key: string]: string | number }>> = {};

        for (const cluster of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
            const catScores = categoryScoresByCluster[cluster] || [];
            result[cluster] = catScores.map(cs => {
                const dataPoint: { category: string; score: number; fullMark: number;[key: string]: string | number } = {
                    category: cs.category,
                    score: cs.score ?? 0,
                    fullMark: 5,
                };

                // Add comparison scores
                visibleComparisons.forEach((comp, idx) => {
                    const compCatScores = comparisonCategoryScoresByCluster.get(comp.id)?.[cluster] || [];
                    const compCatScore = compCatScores.find(c => c.category === cs.category);
                    dataPoint[`comp_${idx}`] = compCatScore?.score ?? 0;
                });

                return dataPoint;
            });
        }



        return result;
    }, [categoryScoresByCluster, visibleComparisons, comparisonCategoryScoresByCluster]);

    // Prepare overlay radar chart data (all clusters combined)
    const overlayRadarData = useMemo(() => {
        // Find the cluster with the most categories for the base
        const allCategories = new Set<string>();
        for (const cluster of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
            const catScores = categoryScoresByCluster[cluster] || [];
            catScores.forEach(cs => allCategories.add(`${cluster}: ${cs.category}`));
        }

        // Create combined data with comparison data
        const data: Array<{
            category: string;
            ENVIRONMENT: number;
            DESIGN: number;
            ECONOMIC: number;
            SOCIAL: number;
            fullMark: number;
            [key: string]: string | number;
        }> = [];

        for (const cluster of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
            const catScores = categoryScoresByCluster[cluster] || [];
            catScores.forEach(cs => {
                const dataPoint: {
                    category: string;
                    ENVIRONMENT: number;
                    DESIGN: number;
                    ECONOMIC: number;
                    SOCIAL: number;
                    fullMark: number;
                    [key: string]: string | number;
                } = {
                    category: cs.category,
                    ENVIRONMENT: cluster === 'ENVIRONMENT' ? (cs.score ?? 0) : 0,
                    DESIGN: cluster === 'DESIGN' ? (cs.score ?? 0) : 0,
                    ECONOMIC: cluster === 'ECONOMIC' ? (cs.score ?? 0) : 0,
                    SOCIAL: cluster === 'SOCIAL' ? (cs.score ?? 0) : 0,
                    fullMark: 5,
                };

                // Add comparison scores for each cluster
                visibleComparisons.forEach((comp, idx) => {
                    const compCatScores = comparisonCategoryScoresByCluster.get(comp.id)?.[cluster] || [];
                    const compCatScore = compCatScores.find(c => c.category === cs.category);
                    // Use cluster-prefixed keys for comparison: COMP0_ENVIRONMENT, COMP0_DESIGN, etc.
                    dataPoint[`COMP${idx}_${cluster}`] = compCatScore?.score ?? 0;
                });

                data.push(dataPoint);
            });
        }

        return data;
    }, [categoryScoresByCluster, visibleComparisons, comparisonCategoryScoresByCluster]);

    // Prepare drill-down data for a single category (showing subcategories)
    const drillDownData = useMemo(() => {
        if (!selectedCategory) return null;

        const { cluster, category } = selectedCategory;
        const subData = scoreRows.filter(r => r.cluster === cluster && r.category === category);

        return subData.map(r => {
            const dataPoint: { name: string; score: number; fullMark: number;[key: string]: string | number } = {
                name: r.subcategory,
                score: r.effectiveScore ?? 0,
                fullMark: 5
            };

            // Add comparison scores for subcategories
            visibleComparisons.forEach((comp, idx) => {
                // Find the comparison score for this subcategory
                const compScore = comp.scores.find(
                    s => s.cluster === cluster &&
                        s.category_name === r.enCategory &&
                        s.subcategory_name === r.enSubcategory
                );
                if (compScore?.score != null) {
                    dataPoint[`comp_${idx}`] = compScore.score * compScore.reliability;
                } else {
                    dataPoint[`comp_${idx}`] = 0;
                }
            });

            return dataPoint;
        });
    }, [selectedCategory, scoreRows, visibleComparisons]);

    // Custom tick component for clickable category names
    const WrappedText = ({ text, x, y, fontSize, color, anchor = "middle" }: any) => {
        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = "";

        // Simple word wrap logic
        words.forEach((word: string) => {
            if ((currentLine + word).length > 15 && currentLine !== "") {
                lines.push(currentLine.trim());
                currentLine = word + " ";
            } else {
                currentLine += word + " ";
            }
        });
        lines.push(currentLine.trim());

        return (
            <text
                x={x}
                y={y}
                fontSize={fontSize}
                fill={color}
                textAnchor={anchor}
                className="font-medium outline-none group-hover:font-bold transition-all duration-200"
            >
                {lines.map((line, i) => (
                    <tspan x={x} dy={i === 0 ? 0 : fontSize + 2} key={i}>
                        {line}
                    </tspan>
                ))}
            </text>
        );
    };

    const ClickableTick = (props: any) => {
        const { x, y, payload, cx, cy, radius, cluster } = props;

        // Push the tick outward by an offset
        const offset = 12;
        const nx = x + (x - cx) * (offset / radius);
        const ny = y + (y - cy) * (offset / radius);

        return (
            <g
                transform="translate(0,0)"
                onClick={() => {
                    if (cluster) {
                        setSelectedCategory({ cluster, category: payload.value });
                    }
                }}
                className="cursor-pointer group"
            >
                <WrappedText
                    text={payload.value}
                    x={nx}
                    y={ny}
                    fontSize={cluster ? 10 : 10}
                    color="#64748b"
                />
            </g>
        );
    };

    const SimpleTick = (props: any) => {
        const { x, y, cx, cy, radius, payload } = props;
        const offset = 14;
        const nx = x + (x - cx) * (offset / radius);
        const ny = y + (y - cy) * (offset / radius);

        return (
            <g transform="translate(0,0)" className="group">
                <WrappedText
                    text={payload.value}
                    x={nx}
                    y={ny}
                    fontSize={props.fontSize || 11}
                    color="#64748b"
                />
            </g>
        );
    };

    // Get grade info
    const gradeInfo = getGrade(overallScore);

    if (loading) {
        if (hideSelectionControls) return null;
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-black" />
            </div>
        );
    }

    return (
        <div className="flex flex-col w-full">
            {/* Row 1: Summary and Overlay Chart Side by Side */}
            <div className="flex h-screen gap-4 p-4">
                {/* Executive Summary Header - Vertical Layout */}
                <div className="flex flex-col gap-4 py-4 px-6 w-[260px] shrink-0">
                    {/* Overall Score */}
                    <div className="text-center">
                        <div className="text-3xl font-bold text-black">
                            {overallScore?.toFixed(1) ?? '—'}
                        </div>
                        <div className="text-sm text-slate-600 uppercase tracking-wide">
                            {t('overallScore')}
                        </div>
                        {comparisonSummaryScores.length > 0 && (
                            <div className="mt-1 flex flex-col gap-0.5">
                                {comparisonSummaryScores.map((comp) => (
                                    <div key={comp.id} className="text-sm font-semibold" style={{ color: comparisonColorMap.get(comp.id) }}>
                                        {comp.overall?.toFixed(1) ?? '—'}
                                        <span className="text-[10px] font-normal text-slate-400 ml-1 truncate">{comp.name}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="w-full h-px bg-slate-200" />

                    {/* Grade */}
                    <div className="text-center relative group cursor-help">
                        <div className={`text-3xl font-bold ${gradeInfo.color}`}>
                            {gradeInfo.grade}
                        </div>
                        <div className="text-sm text-slate-600 uppercase tracking-wide flex items-center gap-1 justify-center">
                            {t('grade')}
                            <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        {/* Grading Scale Tooltip */}
                        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                            <div className="bg-white/80 backdrop-blur-md rounded-lg shadow-lg border border-white/20 p-3 whitespace-nowrap">
                                <div className="text-xs font-semibold text-slate-700 mb-2">{t('gradingScale')}</div>
                                <div className="space-y-1 text-xs">
                                    <div className="flex items-center gap-2"><span className="w-5 h-5 rounded bg-emerald-100 text-emerald-700 text-center font-bold text-[10px] leading-5">A</span> <span className="text-slate-600">4.5–5.0</span></div>
                                    <div className="flex items-center gap-2"><span className="w-5 h-5 rounded bg-green-100 text-green-700 text-center font-bold text-[10px] leading-5">B</span> <span className="text-slate-600">4.0–4.5</span></div>
                                    <div className="flex items-center gap-2"><span className="w-5 h-5 rounded bg-yellow-100 text-yellow-700 text-center font-bold text-[10px] leading-5">C</span> <span className="text-slate-600">3.0–4.0</span></div>
                                    <div className="flex items-center gap-2"><span className="w-5 h-5 rounded bg-orange-100 text-orange-700 text-center font-bold text-[10px] leading-5">D</span> <span className="text-slate-600">2.0–3.0</span></div>
                                    <div className="flex items-center gap-2"><span className="w-5 h-5 rounded bg-red-100 text-red-700 text-center font-bold text-[10px] leading-5">E</span> <span className="text-slate-600">0.0–2.0</span></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="w-full h-px bg-slate-200" />

                    {/* Cluster Scores - Vertical Stack */}
                    <div className="flex flex-col gap-3">
                        {(['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL'] as const).map((cluster) => (
                            <div
                                key={cluster}
                                className="text-center px-2"
                            >
                                <div className="text-base font-bold leading-tight" style={{ color: CLUSTER_COLORS[cluster] }}>
                                    {clusterScores[cluster]?.score?.toFixed(1) ?? '—'}
                                </div>
                                <div className="text-[10px] text-slate-600 uppercase tracking-wide leading-tight">
                                    {tStage2(cluster.toLowerCase() as any)}
                                </div>
                                <div className="text-[9px] text-slate-400 leading-tight">
                                    {clusterScores[cluster]?.count}/{clusterScores[cluster]?.total}
                                </div>
                                {comparisonSummaryScores.length > 0 && (
                                    <div className="mt-0.5 flex flex-col gap-0.5">
                                        {comparisonSummaryScores.map((comp) => (
                                            <div key={comp.id} className="text-[11px] font-semibold leading-tight" style={{ color: comparisonColorMap.get(comp.id) }}>
                                                {comp.clusters[cluster]?.score?.toFixed(1) ?? '—'}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Comparison Section */}
                    <div className="w-full h-px bg-slate-200" />
                    <div className="flex flex-col gap-2">
                        {/* Benchmark toggle — hidden in read-only compare mode */}
                        {!hideSelectionControls && (
                            <div className="flex items-center justify-between">
                                <div className="flex bg-slate-100 p-0.5 rounded">
                                    <button
                                        onClick={() => setFilterMode('all')}
                                        className={`px-3 py-1 text-xs font-medium transition-all duration-200 rounded-sm cursor-pointer ${filterMode === 'all'
                                            ? 'bg-white text-slate-800 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                            }`}
                                    >
                                        All
                                    </button>
                                    <button
                                        onClick={() => setFilterMode('same_type')}
                                        disabled={!weightProfileId}
                                        className={`px-3 py-1 text-xs font-medium transition-all duration-200 rounded-sm whitespace-nowrap ${filterMode === 'same_type'
                                            ? 'bg-white text-slate-800 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                            } ${!weightProfileId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        title={!weightProfileId ? 'No typology assigned' : 'Same typology'}
                                    >
                                        Same Type
                                    </button>
                                </div>
                            </div>
                        )}
                        <CertificationComparisonSelector className="w-full" vertical={true} />
                    </div>
                </div>

                {/* Overlay Radar Chart - All Clusters Combined */}
                <div className="flex-1 overflow-hidden -mt-24">
                    <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={overlayRadarData} margin={{ top: 0, right: 0, bottom: 40, left: 0 }}>
                            <PolarGrid stroke="#e2e8f0" />
                            <PolarAngleAxis
                                dataKey="category"
                                tick={<SimpleTick fontSize={11} />}
                                axisLine={false}
                                tickLine={false}
                            />
                            <PolarRadiusAxis
                                angle={90}
                                domain={[0, 5]}
                                tick={{ fontSize: 11, fill: '#94a3b8' }}
                                tickCount={6}
                            />
                            <Radar
                                name={tStage2('environment')}
                                dataKey="ENVIRONMENT"
                                stroke={CLUSTER_COLORS['ENVIRONMENT']}
                                fill={CLUSTER_COLORS['ENVIRONMENT']}
                                fillOpacity={0.2}
                                strokeWidth={2}
                                isAnimationActive={false}
                            />
                            <Radar
                                name={tStage2('design')}
                                dataKey="DESIGN"
                                stroke={CLUSTER_COLORS['DESIGN']}
                                fill={CLUSTER_COLORS['DESIGN']}
                                fillOpacity={0.2}
                                strokeWidth={2}
                                isAnimationActive={false}
                            />
                            <Radar
                                name={tStage2('economic')}
                                dataKey="ECONOMIC"
                                stroke={CLUSTER_COLORS['ECONOMIC']}
                                fill={CLUSTER_COLORS['ECONOMIC']}
                                fillOpacity={0.2}
                                strokeWidth={2}
                                isAnimationActive={false}
                            />
                            <Radar
                                name={tStage2('social')}
                                dataKey="SOCIAL"
                                stroke={CLUSTER_COLORS['SOCIAL']}
                                fill={CLUSTER_COLORS['SOCIAL']}
                                fillOpacity={0.2}
                                strokeWidth={2}
                                isAnimationActive={false}
                            />
                            {/* Comparison Radars for overlay chart */}
                            {visibleComparisons[0] && (() => {
                                const c0 = comparisonColorMap.get(visibleComparisons[0].id) || COMPARISON_COLORS[0]; return (
                                    <>
                                        <Radar name={`${visibleComparisons[0].name} (Env)`} dataKey="COMP0_ENVIRONMENT" stroke={c0} fill={c0} fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" isAnimationActive={false} />
                                        <Radar name={`${visibleComparisons[0].name} (Des)`} dataKey="COMP0_DESIGN" stroke={c0} fill={c0} fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" isAnimationActive={false} />
                                        <Radar name={`${visibleComparisons[0].name} (Eco)`} dataKey="COMP0_ECONOMIC" stroke={c0} fill={c0} fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" isAnimationActive={false} />
                                        <Radar name={`${visibleComparisons[0].name} (Soc)`} dataKey="COMP0_SOCIAL" stroke={c0} fill={c0} fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" isAnimationActive={false} />
                                    </>
                                );
                            })()}
                            {visibleComparisons[1] && (() => {
                                const c1 = comparisonColorMap.get(visibleComparisons[1].id) || COMPARISON_COLORS[1]; return (
                                    <>
                                        <Radar name={`${visibleComparisons[1].name} (Env)`} dataKey="COMP1_ENVIRONMENT" stroke={c1} fill={c1} fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" isAnimationActive={false} />
                                        <Radar name={`${visibleComparisons[1].name} (Des)`} dataKey="COMP1_DESIGN" stroke={c1} fill={c1} fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" isAnimationActive={false} />
                                        <Radar name={`${visibleComparisons[1].name} (Eco)`} dataKey="COMP1_ECONOMIC" stroke={c1} fill={c1} fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" isAnimationActive={false} />
                                        <Radar name={`${visibleComparisons[1].name} (Soc)`} dataKey="COMP1_SOCIAL" stroke={c1} fill={c1} fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" isAnimationActive={false} />
                                    </>
                                );
                            })()}
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    backdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    whiteSpace: 'nowrap'
                                }}
                            />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Individual Radar Charts - 2x2 Grid */}
            <div id="radar-charts-grid" className="h-[90vh] overflow-hidden">
                <div className="grid grid-cols-2 h-full w-full">
                    {(['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL'] as const).map((cluster) => {
                        const isDrilledDown = selectedCategory?.cluster === cluster;

                        return (
                            <div
                                key={cluster}
                                className="p-1 bg-transparent relative flex flex-col h-full transition-all duration-300"
                            >
                                <div className="flex items-center justify-between h-6 px-2">
                                    <h4
                                        className={`text-xs font-bold uppercase tracking-wider truncate mr-2 transition-colors ${isDrilledDown ? 'cursor-pointer hover:opacity-80' : ''}`}
                                        style={{ color: CLUSTER_COLORS[cluster] }}
                                        title={isDrilledDown ? selectedCategory.category : tStage2(cluster.toLowerCase() as any)}
                                        onClick={(e) => {
                                            if (isDrilledDown) {
                                                e.stopPropagation();
                                                setSelectedCategory(null);
                                            }
                                        }}
                                    >
                                        {isDrilledDown ? selectedCategory.category : tStage2(cluster.toLowerCase() as any)}
                                    </h4>
                                </div>
                                <div className="flex-1 min-h-0">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <RadarChart
                                            data={isDrilledDown ? (drillDownData || []) : radarDataByCluster[cluster]}
                                            margin={{ top: 10, right: 30, bottom: 10, left: 30 }}
                                        >
                                            <PolarGrid stroke="#cbd5e1" />
                                            <PolarAngleAxis
                                                dataKey={isDrilledDown ? "name" : "category"}
                                                tick={isDrilledDown ? <SimpleTick /> : <ClickableTick cluster={cluster} />}
                                                axisLine={false}
                                                tickLine={false}
                                            />
                                            <PolarRadiusAxis
                                                angle={90}
                                                domain={[0, 5]}
                                                tick={{ fontSize: 10, fill: '#94a3b8' }}
                                                tickCount={6}
                                            />
                                            {/* Main certification Radar */}
                                            <Radar
                                                name="Score"
                                                dataKey="score"
                                                stroke={CLUSTER_COLORS[cluster]}
                                                fill={CLUSTER_COLORS[cluster]}
                                                fillOpacity={0.3}
                                                strokeWidth={2}
                                                dot={{ r: isDrilledDown ? 3.5 : 3, fill: CLUSTER_COLORS[cluster] }}
                                                isAnimationActive={false}
                                            />
                                            {/* Comparison Radars - show in both category and drilldown views */}
                                            {visibleComparisons[0] && (
                                                <Radar
                                                    name={visibleComparisons[0].name}
                                                    dataKey="comp_0"
                                                    stroke={comparisonColorMap.get(visibleComparisons[0].id) || COMPARISON_COLORS[0]}
                                                    fill={comparisonColorMap.get(visibleComparisons[0].id) || COMPARISON_COLORS[0]}
                                                    fillOpacity={0.1}
                                                    strokeWidth={1.5}
                                                    strokeDasharray="4 2"
                                                    dot={{ r: 2, fill: COMPARISON_COLORS[0] }}
                                                    isAnimationActive={false}
                                                />
                                            )}
                                            {visibleComparisons[1] && (
                                                <Radar
                                                    name={visibleComparisons[1].name}
                                                    dataKey="comp_1"
                                                    stroke={comparisonColorMap.get(visibleComparisons[1].id) || COMPARISON_COLORS[1]}
                                                    fill={comparisonColorMap.get(visibleComparisons[1].id) || COMPARISON_COLORS[1]}
                                                    fillOpacity={0.1}
                                                    strokeWidth={1.5}
                                                    strokeDasharray="4 2"
                                                    dot={{ r: 2, fill: comparisonColorMap.get(visibleComparisons[1].id) || COMPARISON_COLORS[1] }}
                                                    isAnimationActive={false}
                                                />
                                            )}
                                            <Tooltip
                                                formatter={(value) => [typeof value === 'number' ? value.toFixed(1) : String(value), 'Score']}
                                                contentStyle={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                    backdropFilter: 'blur(8px)',
                                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                                    borderRadius: '8px',
                                                    fontSize: '11px',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            />
                                        </RadarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export const ScoreReport = memo(ScoreReportComponent);
